// src/components/PatientForm.tsx
import React, { useState, useEffect } from "react";
import API_BASE_URL from "../config/apiConfig";
import { useAuth } from "../context/AuthProvider";
import Swal from "sweetalert2";

export interface CreatedPatient {
  id: string;
  firstname: string;
  lastname: string;
  cnp?: string;
}

interface PatientFormProps {
  onSuccess?: (patient: CreatedPatient) => void;
  onCancel?: () => void;
  hideCancel?: boolean;
  /** Layout compact cu secțiuni - pentru modal */
  layout?: "default" | "sectioned";
}

const PatientForm: React.FC<PatientFormProps> = ({
  onSuccess,
  onCancel,
  hideCancel = false,
  layout = "default",
}) => {
  const { token } = useAuth();
  const isSectioned = layout === "sectioned";

  const [mode, setMode] = useState<"full" | "consult">("full");
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    cnp: "",
    type: "",
    das: "",
    age_range: "",
    category: "",
    parentFirstName: "",
    parentLastName: "",
    parentEmail: "",
    parentPhone: "",
    country: "",
    zone: "",
    city: "",
  });

  const [patientTypes, setPatientTypes] = useState<{ key: string; value: string }[]>([]);
  const [ageRanges, setAgeRanges] = useState<{ key: string; value: string }[]>([]);
  const [categories, setCategories] = useState<{ key: string; value: string }[]>([]);
  const [allCountries, setAllCountries] = useState<{ key: string; value: string }[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<{ key: string; value: string }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true,
  });

  useEffect(() => {
    if (!token) return;
    const fetchDropdowns = async () => {
      try {
        const fetchData = async (endpoint: string) => {
          const r = await fetch(`${API_BASE_URL}/types/${endpoint}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await r.json();
          return data.data;
        };
        const [types, ranges, cats] = await Promise.all([
          fetchData("patient-types"),
          fetchData("age-ranges"),
          fetchData("categories"),
        ]);
        setPatientTypes(types || []);
        setAgeRanges(ranges || []);
        setCategories(cats || []);
      } catch {
        toast.fire({ icon: "error", title: "Eroare la încărcarea listelor" });
      }
    };
    fetchDropdowns();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const fetchCountries = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/types/get-countries`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const countries = Array.isArray(data) ? data : data?.data || [];
        setAllCountries(countries);
      } catch {
        toast.fire({ icon: "error", title: "Eroare la încărcarea țărilor." });
      }
    };
    fetchCountries();
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((p) => ({ ...p, country: value }));
    if (value.length > 1) {
      setFilteredCountries(
        allCountries.filter((c) => c.value.toLowerCase().includes(value.toLowerCase()))
      );
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleCountrySelect = (country: string) => {
    setFormData((p) => ({ ...p, country }));
    setShowSuggestions(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    try {
      setSubmitting(true);
      let url = "";
      let payload: any = {};

      if (mode === "consult") {
        if (!formData.firstname || !formData.lastname || !formData.parentPhone) {
          toast.fire({ icon: "error", title: "Completează nume, prenume și telefon." });
          setSubmitting(false);
          return;
        }
        url = `${API_BASE_URL}/patient/add-consult`;
        payload = {
          firstname: formData.firstname,
          lastname: formData.lastname,
          parentPhone: formData.parentPhone,
        };
      } else {
        const cnpRegex = /^[0-9]{13}$/;
        if (!cnpRegex.test(formData.cnp)) {
          toast.fire({ icon: "error", title: "CNP-ul trebuie să conțină exact 13 cifre." });
          setSubmitting(false);
          return;
        }
        url = `${API_BASE_URL}/patient/add`;
        payload = formData;
      }

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || "A apărut o eroare.");

      toast.fire({ icon: "success", title: "Pacient adăugat cu succes!" });

      const created: CreatedPatient = {
        id: String(
        data?.pacient?.id ||      // ← format consult
        data?.patient?.id ||      // ← variantă engleză
        data?.id ||
        data?.patient_id ||
        data?.data?.id ||
        ""
        ),
        firstname: formData.firstname,
        lastname: formData.lastname,
        cnp: formData.cnp || undefined,
      };
      onSuccess?.(created);
    } catch (err: any) {
      toast.fire({ icon: "error", title: err.message || "Eroare la adăugare." });
    } finally {
      setSubmitting(false);
    }
  };

  /* ════════════════════════════════════════════════════════════════
     LAYOUT CU SECȚIUNI (pentru modal)
     ════════════════════════════════════════════════════════════════ */
  if (isSectioned) {
    return (
      <form onSubmit={handleSubmit} className="pf-sectioned">
        {/* ─── Tip adăugare ─── */}
        <div className="pf-mode-bar">
          <label className={`pf-mode-pill ${mode === "full" ? "active" : ""}`}>
            <input
              type="radio"
              checked={mode === "full"}
              onChange={() => setMode("full")}
            />
            <span>Pacient normal</span>
          </label>
          <label className={`pf-mode-pill ${mode === "consult" ? "active" : ""}`}>
            <input
              type="radio"
              checked={mode === "consult"}
              onChange={() => setMode("consult")}
            />
            <span>Consult medical (rapid)</span>
          </label>
        </div>

        {/* ═══ SECȚIUNE: Date pacient ═══ */}
        <section className="pf-section">
          <h3 className="pf-section-title">Date pacient</h3>
          <div className="pf-grid pf-grid-2">
            <div className="pf-field">
              <label>Nume</label>
              <input
                type="text"
                name="lastname"
                value={formData.lastname}
                onChange={handleChange}
                placeholder="Numele"
                required
              />
            </div>
            <div className="pf-field">
              <label>Prenume</label>
              <input
                type="text"
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                placeholder="Prenumele"
                required
              />
            </div>
          </div>

          {mode === "consult" && (
            <div className="pf-grid pf-grid-1">
              <div className="pf-field">
                <label>Telefon</label>
                <input
                  type="text"
                  name="parentPhone"
                  value={formData.parentPhone}
                  onChange={handleChange}
                  placeholder="Telefon"
                  required
                />
              </div>
            </div>
          )}

          {mode === "full" && (
            <>
              <div className="pf-grid pf-grid-2">
                <div className="pf-field">
                  <label>CNP</label>
                  <input
                    type="text"
                    name="cnp"
                    value={formData.cnp}
                    onChange={handleChange}
                    placeholder="13 cifre"
                    maxLength={13}
                    required
                  />
                </div>
                <div className="pf-field">
                  <label>Tip</label>
                  <select name="type" value={formData.type} onChange={handleChange} required>
                    <option value="">Selectează tipul</option>
                    {patientTypes.map((t) => (
                      <option key={t.key} value={t.key}>{t.value}</option>
                    ))}
                  </select>
                </div>
              </div>

              {formData.type === "intern" && (
                <div className="pf-grid pf-grid-1">
                  <div className="pf-field">
                    <label>DAS</label>
                    <input
                      type="text"
                      name="das"
                      value={formData.das}
                      onChange={handleChange}
                      placeholder="DAS"
                    />
                  </div>
                </div>
              )}

              <div className="pf-grid pf-grid-2">
                <div className="pf-field">
                  <label>Vârstă</label>
                  <select name="age_range" value={formData.age_range} onChange={handleChange} required>
                    <option value="">Selectează vârsta</option>
                    {ageRanges.map((r) => (
                      <option key={r.key} value={r.key}>{r.value}</option>
                    ))}
                  </select>
                </div>
                <div className="pf-field">
                  <label>Categorie</label>
                  <select name="category" value={formData.category} onChange={handleChange} required>
                    <option value="">Selectează categoria</option>
                    {categories.map((c) => (
                      <option key={c.key} value={c.key}>{c.value}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
        </section>

        {/* ═══ SECȚIUNE: Reprezentant legal (doar pt full) ═══ */}
        {mode === "full" && (
          <section className="pf-section">
            <h3 className="pf-section-title">Reprezentant legal</h3>
            <div className="pf-grid pf-grid-2">
              <div className="pf-field">
                <label>Nume</label>
                <input
                  type="text"
                  name="parentLastName"
                  value={formData.parentLastName}
                  onChange={handleChange}
                  placeholder="Nume reprezentant"
                  required
                />
              </div>
              <div className="pf-field">
                <label>Prenume</label>
                <input
                  type="text"
                  name="parentFirstName"
                  value={formData.parentFirstName}
                  onChange={handleChange}
                  placeholder="Prenume reprezentant"
                  required
                />
              </div>
            </div>
            <div className="pf-grid pf-grid-2">
              <div className="pf-field">
                <label>Email</label>
                <input
                  type="email"
                  name="parentEmail"
                  value={formData.parentEmail}
                  onChange={handleChange}
                  placeholder="email@exemplu.ro"
                  required
                />
              </div>
              <div className="pf-field">
                <label>Telefon</label>
                <input
                  type="text"
                  name="parentPhone"
                  value={formData.parentPhone}
                  onChange={handleChange}
                  placeholder="Telefon"
                  required
                />
              </div>
            </div>
          </section>
        )}

        {/* ═══ SECȚIUNE: Adresă (doar pt full) ═══ */}
        {mode === "full" && (
          <section className="pf-section">
            <h3 className="pf-section-title">Adresă</h3>
            <div className="pf-grid pf-grid-1 pf-relative">
              <div className="pf-field autocomplete-wrapper">
                <label>Țară</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleCountryChange}
                  placeholder="Țara"
                  autoComplete="off"
                  required
                />
                {showSuggestions && filteredCountries.length > 0 && (
                  <ul className="autocomplete-list">
                    {filteredCountries.map((c) => (
                      <li key={c.key} onClick={() => handleCountrySelect(c.value)}>
                        {c.value}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="pf-grid pf-grid-2">
              <div className="pf-field">
                <label>Județ / Regiune</label>
                <input
                  type="text"
                  name="zone"
                  value={formData.zone}
                  onChange={handleChange}
                  placeholder="Județ / Regiune"
                  required
                />
              </div>
              <div className="pf-field">
                <label>Localitate</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Localitate"
                  required
                />
              </div>
            </div>
          </section>
        )}

        {/* ─── Actions ─── */}
        <div className="pf-actions">
          {!hideCancel && (
            <button className="cancel-btn" onClick={onCancel} type="button">
              Anulează
            </button>
          )}
          <button className="primary-btn" type="submit" disabled={submitting}>
            {submitting ? "Se salvează..." : "Adaugă pacient"}
          </button>
        </div>
      </form>
    );
  }

  /* ════════════════════════════════════════════════════════════════
     LAYOUT DEFAULT (NESCHIMBAT pt pagina AddPatient)
     ════════════════════════════════════════════════════════════════ */
  return (
    <form onSubmit={handleSubmit}>
      <div className="form-row">
        <p>Tip adăugare:</p>
        <div className="tip-adaugare-pacient">
          <label>
            <input type="radio" checked={mode === "full"} onChange={() => setMode("full")} />{" "}
            Pacient normal
          </label>
          <label>
            <input type="radio" checked={mode === "consult"} onChange={() => setMode("consult")} />{" "}
            Consult medical (rapid)
          </label>
        </div>
      </div>

      <div className="form-row">
        <label>Nume:</label>
        <input type="text" name="lastname" value={formData.lastname} onChange={handleChange} placeholder="Numele" required />
      </div>

      <div className="form-row">
        <label>Prenume:</label>
        <input type="text" name="firstname" value={formData.firstname} onChange={handleChange} placeholder="Prenumele" required />
      </div>

      {mode === "consult" && (
        <div className="form-row">
          <label>Telefon:</label>
          <input type="text" name="parentPhone" value={formData.parentPhone} onChange={handleChange} placeholder="Telefon" required />
        </div>
      )}

      {mode === "full" && (
        <>
          <div className="form-row">
            <label>CNP:</label>
            <input type="text" name="cnp" value={formData.cnp} onChange={handleChange} placeholder="CNP" maxLength={13} required />
          </div>

          <div className="form-row">
            <label>Tip:</label>
            <select name="type" value={formData.type} onChange={handleChange} required>
              <option value="">Selectează tipul</option>
              {patientTypes.map((t) => <option key={t.key} value={t.key}>{t.value}</option>)}
            </select>
          </div>

          {formData.type === "intern" && (
            <div className="form-row">
              <label>DAS:</label>
              <input type="text" name="das" value={formData.das} onChange={handleChange} placeholder="DAS" />
            </div>
          )}

          <div className="form-row flex-row">
            <div className="flex-input">
              <label>Vârstă:</label>
              <select name="age_range" value={formData.age_range} onChange={handleChange} required>
                <option value="">Selectează vârsta</option>
                {ageRanges.map((r) => <option key={r.key} value={r.key}>{r.value}</option>)}
              </select>
            </div>
            <div className="flex-input">
              <label>Categorie:</label>
              <select name="category" value={formData.category} onChange={handleChange} required>
                <option value="">Selectează categoria</option>
                {categories.map((c) => <option key={c.key} value={c.key}>{c.value}</option>)}
              </select>
            </div>
          </div>

          <h3 className="form-subtitle">Reprezentant Legal</h3>

          <div className="form-row flex-row">
            <div className="flex-input">
              <label>Nume:</label>
              <input type="text" name="parentLastName" value={formData.parentLastName} onChange={handleChange} placeholder="Nume reprezentant legal" required />
            </div>
            <div className="flex-input">
              <label>Prenume:</label>
              <input type="text" name="parentFirstName" value={formData.parentFirstName} onChange={handleChange} placeholder="Prenume reprezentant legal" required />
            </div>
          </div>

          <div className="form-row flex-row">
            <div className="flex-input">
              <label>Email:</label>
              <input type="email" name="parentEmail" value={formData.parentEmail} onChange={handleChange} placeholder="Adresa de e-mail" required />
            </div>
            <div className="flex-input">
              <label>Telefon:</label>
              <input type="text" name="parentPhone" value={formData.parentPhone} onChange={handleChange} placeholder="Telefon" required />
            </div>
          </div>

          <div className="form-row autocomplete-wrapper">
            <label>Țară:</label>
            <input type="text" name="country" value={formData.country} onChange={handleCountryChange} placeholder="Țara" autoComplete="off" required />
            {showSuggestions && filteredCountries.length > 0 && (
              <ul className="autocomplete-list">
                {filteredCountries.map((c) => (
                  <li key={c.key} onClick={() => handleCountrySelect(c.value)}>{c.value}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="form-row flex-row">
            <div className="flex-input">
              <label>Județ/Regiune:</label>
              <input type="text" name="zone" value={formData.zone} onChange={handleChange} placeholder="Județ/Regiune" required />
            </div>
            <div className="flex-input">
              <label>Localitate:</label>
              <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="Localitate" required />
            </div>
          </div>
        </>
      )}

      <div className="form-row form-actions">
        {!hideCancel && (
          <button className="cancel-btn" onClick={onCancel} type="button">Anulează</button>
        )}
        <button className="primary-btn" type="submit" disabled={submitting}>
          {submitting ? "Se salvează..." : "Adaugă pacient"}
        </button>
      </div>
    </form>
  );
};

export default PatientForm;